/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { ApiStatus } from '../../../../../ts';
import { Skill, WorkspaceStatus } from '../../../../../ts/skill';
import { CreateAsyncThunk, SkillsSlice } from '../../../../../ts/state';
import * as API from '../../../api/skills';
import { selectIsAccessUpdatesEnabled } from '../../features/selectors';
import { selectIsNewIa } from '../../instance/selectors';
import { addErrorMessage } from '../../messages/actions';
import { getAssistantId } from '../../session/selectors';
import { entityAdapter } from '../adapter';
import {
  selectTrainingCheckStatusForSkillId,
  selectTestingCheckStatusForSkillId
} from '../selectors';

export const getSkill = createAsyncThunk<Skill, string, CreateAsyncThunk>(
  'skills/get',
  async (id, { dispatch, getState }) => {
    try {
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listVariable
      const assistantId = getAssistantId(getState());
      const isNewIA = selectIsNewIa(getState());
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(getState());
      const response = await API.getSkill(
        id,
        useNewIAUpdatedAccess,
        assistantId
      );
      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

/** Returns the training status for a given skill */
export const getTrainingStatus = createAsyncThunk<
  WorkspaceStatus,
  string,
  CreateAsyncThunk
>(
  'skills/status',
  async (skillId, { getState }) => {
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to getSkillStatus
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    return await API.getSkillStatus(
      skillId,
      useNewIAUpdatedAccess,
      assistantId
    );
  },
  {
    condition: (skillId, { getState }) =>
      skillId &&
      selectTrainingCheckStatusForSkillId(getState(), skillId) !==
        ApiStatus.LOADING
  }
);

export const getTestingStatus = createAsyncThunk<
  WorkspaceStatus,
  string,
  CreateAsyncThunk
>(
  'skills/testingStatus',
  async (skillId, { getState }) => {
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const response = await API.getSkillStatus(
      skillId,
      useNewIAUpdatedAccess,
      assistantId
    );

    return response;
  },
  {
    condition: (skillId, { getState }) =>
      skillId &&
      selectTestingCheckStatusForSkillId(getState(), skillId) !==
        ApiStatus.LOADING
  }
);
export const clearTrainingStatus = createAsyncThunk<
  void,
  null,
  CreateAsyncThunk
>('skills/clearTrainingStatus', () => {}, {
  condition: (skillId, { getState }) =>
    skillId &&
    selectTestingCheckStatusForSkillId(getState(), skillId) !==
      ApiStatus.LOADING
});

const reducer = (builder: ActionReducerMapBuilder<SkillsSlice>) => {
  builder
    .addCase(getSkill.pending, (state, _action) => {
      state.getStatus = ApiStatus.LOADING;
      state.getError = null;
    })
    .addCase(getSkill.fulfilled, (state, action) => {
      state.getStatus = ApiStatus.SUCCEEDED;
      state.getError = null;
      entityAdapter.upsertOne(state, action.payload);
    })
    .addCase(getSkill.rejected, (state, action) => {
      state.getStatus = ApiStatus.ERROR;
      state.getError = action.error;
    })
    .addCase(getTrainingStatus.pending, (state, action) => {
      const id = action.meta.arg;
      state.trainingStatus[id] = ApiStatus.LOADING;
      state.trainingError[id] = null;
    })
    .addCase(getTrainingStatus.fulfilled, (state, action) => {
      const id = action.meta.arg;
      state.trainingStatus[id] = ApiStatus.SUCCEEDED;
      state.trainingError[id] = null;
      state.training[id] = action.payload.training;
    })
    .addCase(getTrainingStatus.rejected, (state, action) => {
      const id = action.meta.arg;
      state.trainingStatus[id] = ApiStatus.ERROR;
      state.trainingError[id] = action.error;
    })
    .addCase(getTestingStatus.pending, (state, action) => {
      const id = action.meta.arg;
      state.testingStatus = ApiStatus.LOADING;
      state.testingError[id] = null;
    })
    .addCase(getTestingStatus.fulfilled, (state, action) => {
      const id = action.meta.arg;
      state.testingStatus = ApiStatus.SUCCEEDED;
      state.testingError[id] = null;
      state.testing[id] = action.payload.test_status;
    })
    .addCase(clearTrainingStatus.fulfilled, state => {
      state.testingStatus = ApiStatus.IDLE;
      state.testingError = {};
      state.testing = {};
    })
    .addCase(getTestingStatus.rejected, (state, action) => {
      const id = action.meta.arg;
      state.testingStatus = ApiStatus.ERROR;
      state.testingError[id] = action.error;
    });
};

export default reducer;
